import {
    Alert,
    Anchor,
    Code,
    Group,
    List,
    ScrollArea,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import {NavLink} from 'react-router-dom';
import {
    PlatformPropertyCard,
    PlatformPropertyCardEntryInput,
} from '@flexinet/ui-components';

import {TestObservation} from '../types';
import {
    OutcomeBadge,
    VerificationBadge,
} from './Badges.tsx';
import {
    classifyObservation,
    dependencyCodes,
    ObservationKind,
    observationKindDisplay,
} from '../utils/classifyObservation.ts';

export const Profile = ({record}: { record: TestObservation }) => {

    const kind = classifyObservation(record);
    const display = observationKindDisplay(kind);
    const dependencies = dependencyCodes(record);

    const identityProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Action Code',
            value: record.actionCode,
            formatter: 'code',
            span: 12,
        }, {
            label: 'Observed At',
            value: record.observedAt,
            formatter: 'timestamp',
            span: 12,
        }, {
            label: 'Duration',
            value: typeof record.durationMs === 'number' ? `${record.durationMs} ms` : 'n/a',
            span: 12,
        }];

    const contextProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Observation ID',
            value: record.id,
            span: 12,
        }, {
            label: 'Recorded By',
            value: record.createdByUsername ?? 'n/a',
            span: 12,
        }, {
            label: 'Created',
            value: record.created,
            formatter: 'timestamp',
            span: 12,
        }];

    return (
        <Stack gap={'xl'}>
            <Stack gap={'xs'}>
                <Group justify={'space-between'}>
                    <Text size={'sm'} c={'dimmed'}>Outcome</Text>
                    <OutcomeBadge record={record} kind={kind}/>
                </Group>
                <Group justify={'space-between'}>
                    <Text size={'sm'} c={'dimmed'}>Verification</Text>
                    <VerificationBadge verificationResult={record.verificationResult}/>
                </Group>
                {/* Say plainly what this classification MEANS — the whole point is that
                    "SKIPPED" alone does not tell a triager whether to act. */}
                <Text size={'xs'} c={'dimmed'}>{display.description}</Text>
            </Stack>

            {/* A dependency skip is not a defect. State the root cause prominently so
                nobody investigates this row instead of the failure that caused it. */}
            {kind === ObservationKind.DEPENDENCY_SKIP && dependencies.length > 0 && (
                <Alert color={'gray'} title={'This action did not run'}>
                    <Stack gap={'xs'}>
                        <Text size={'sm'}>
                            It was blocked by an action it depends on. Investigate the dependency,
                            not this observation.
                        </Text>
                        <List size={'sm'}>
                            {dependencies.map(code => (
                                <List.Item key={code}><Code>{code}</Code></List.Item>
                            ))}
                        </List>
                    </Stack>
                </Alert>
            )}

            {kind === ObservationKind.UNIMPLEMENTED && (
                <Alert color={'blue'} title={'No handler built yet'}>
                    This action code has no executor handler. That is build status, not a
                    platform defect — an unregistered action code is legal and fails open.
                </Alert>
            )}

            {kind === ObservationKind.PASS_UNVERIFIED && (
                <Alert color={'orange'} title={'Worked, but the platform did not record it'}>
                    The interaction itself succeeded and the expected platform state did not
                    follow. This usually points at the recording path rather than the UI.
                </Alert>
            )}

            <PlatformPropertyCard properties={identityProperties}/>

            <Stack gap={'xs'}>
                <Title order={5}>Synthetic User</Title>
                <Anchor
                    component={NavLink}
                    to={`/synthetic-users/${encodeURIComponent(record.syntheticUserId)}`}
                    size={'sm'}
                >
                    {record.syntheticUserId}
                </Anchor>
            </Stack>

            {record.error && (
                <Stack gap={'xs'}>
                    <Title order={5}>Error</Title>
                    <ScrollArea.Autosize mah={300}>
                        <Code block>{record.error}</Code>
                    </ScrollArea.Autosize>
                </Stack>
            )}

            <PlatformPropertyCard properties={contextProperties}/>
        </Stack>
    );
};
